import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { User, Bell, Lock, FileText, HelpCircle, LogOut, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import BottomNav from '../components/BottomNav';

const COLORS = {
  beige: '#F5F3ED',
  navy: '#1a1a3e',
  orange: '#FF8C42',
  white: '#ffffff',
  gray: {
    100: '#f5f7f8',
    200: '#e8ecee',
    400: '#9ba4ab',
    500: '#6b7280',
    600: '#4c5b62'
  },
  error: '#ef4444'
};

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth0();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const settingsSections = [
    {
      title: 'Account',
      items: [
        { icon: User, label: 'Profile', description: 'Manage your personal information', action: () => {} },
        { icon: Lock, label: 'Security', description: 'Password and authentication', action: () => {} },
        { icon: Bell, label: 'Notifications', description: 'Manage your notification preferences', action: () => {} }
      ]
    },
    {
      title: 'Legal & Support',
      items: [
        { icon: FileText, label: 'Privacy Policy', description: 'View our privacy policy', action: () => navigate('/privacy-policy') },
        { icon: FileText, label: 'Terms of Service', description: 'Read our terms', action: () => navigate('/terms-of-service') },
        { icon: FileText, label: 'Disclosures', description: 'Investment disclosures', action: () => navigate('/disclosures') },
        { icon: HelpCircle, label: 'Help & Support', description: 'Get help with Trove', action: () => {} }
      ]
    }
  ];

  return (
    <div style={{ 
      backgroundColor: COLORS.beige, 
      minHeight: '100vh',
      paddingTop: '80px',
      paddingBottom: '100px'
    }}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-5xl font-light mb-4" style={{ 
            fontFamily: 'Georgia, serif', 
            color: COLORS.navy 
          }}>
            Settings
          </h1>
          <p className="text-gray-600 text-lg">
            Manage your account and preferences
          </p>
        </div>

        {/* User Info Card */}
        <Card style={{
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: COLORS.orange,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.white,
              fontSize: '2rem',
              fontWeight: '300',
              fontFamily: 'Georgia, serif'
            }}>
              {localStorage.getItem('userName')?.split(' ').map(n => n[0]).join('') || 'RP'}
            </div>
            <div>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '300',
                color: COLORS.navy,
                marginBottom: '0.25rem',
                fontFamily: 'Georgia, serif'
              }}>
                {user?.name || 'Guest User'}
              </h2>
              <p style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>
                {user?.email || 'No email'}
              </p>
              <div style={{
                marginTop: '0.5rem',
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                background: `${COLORS.orange}20`,
                color: COLORS.orange,
                fontSize: '0.75rem',
                fontWeight: '600'
              }}>
                Premium Member
              </div>
            </div>
          </div>
        </Card>

        {/* Settings Sections */}
        {settingsSections.map((section, sectionIndex) => (
          <div key={sectionIndex} style={{ marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '500',
              color: COLORS.navy,
              marginBottom: '1rem',
              paddingLeft: '0.5rem'
            }}>
              {section.title}
            </h3>
            
            <Card style={{ overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              {section.items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    onClick={item.action}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '1.25rem 1.5rem',
                      cursor: 'pointer',
                      borderBottom: index < section.items.length - 1 ? `1px solid ${COLORS.gray[200]}` : 'none',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = COLORS.gray[100]}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: COLORS.gray[100],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <IconComponent size={20} color={COLORS.navy} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '500',
                        color: COLORS.navy,
                        marginBottom: '0.25rem'
                      }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: COLORS.gray[500] }}>
                        {item.description}
                      </div>
                    </div>
                    <ChevronRight size={20} color={COLORS.gray[400]} />
                  </div>
                );
              })}
            </Card>
          </div>
        ))}

        {/* App Info */}
        <Card style={{ padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '0.875rem', color: COLORS.gray[500], marginBottom: '0.5rem' }}>
              Trove Version 1.0.0
            </p>
            <p style={{ fontSize: '0.75rem', color: COLORS.gray[400] }}>
              © 2025 Trove. All rights reserved.
            </p>
          </div>
        </Card>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            padding: '1rem',
            background: COLORS.error,
            color: COLORS.white,
            border: 'none',
            borderRadius: '0.75rem',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <LogOut size={20} />
          Log Out
        </button>
      </div>

      <BottomNav />
    </div>
  );
};

export default Settings;